import React, { useCallback } from "react";
import { FastaFile, PathTypeSchema } from "@domain/schemas/BI";
import { UploadFastaService } from "@application/useCases";
import { useInjection } from "@presentation/components/dependencyInjection";
import { useNotifications } from "@presentation/components/notifications";
import { useWorkspaceContextStore } from "@presentation/stores/WorkspaceContextStore";
import { SingleFileUpload } from "@presentation/components/uploadDestination/SingleFileUpload";

interface UploadFastaProperties {
  onUploadSuccess?: (uploadedFasta: FastaFile) => void;
}

const UploadFasta: React.FC<UploadFastaProperties> = ({ onUploadSuccess }) => {
  const service = useInjection(UploadFastaService);
  const { pushNotification } = useNotifications();
  const projectId = useWorkspaceContextStore((state) => state.activeProjectId);
  const checkFileExists = useCallback(
    (name: string, rootPath: string | undefined, folder: string | undefined) => service.checkFastaFileExistsAsync(projectId ?? "", name, rootPath, folder),
    [service, projectId],
  );
  return (
    <SingleFileUpload
      key={projectId}
      projectId={projectId}
      pathType={PathTypeSchema.Fasta}
      allowedExtensions={[".fasta", ".bgsfasta"]}
      maxFileSize={2000 * 1024 * 1024}
      checkFileExists={checkFileExists}
      upload={async (file, destination, onProgress) => {
        if (!projectId) throw new Error("Select a project before uploading a FASTA file.");
        const uploaded = await service.uploadFastaAsync(projectId, file, onProgress, destination.rootPath, destination.folder);
        pushNotification("The FASTA file was successfully uploaded.", "success", 10_000);
        onUploadSuccess?.(uploaded);
      }}
    >
      <p>Select one .fasta or .bgsfasta file (maximum 2 GB), then choose its destination directory.</p>
    </SingleFileUpload>
  );
};

export default UploadFasta;
